/**
 * push-and-friction — drag to push, watch friction take the speed away.
 *
 * This is the sim the reference POC got most right and the one its principles doc criticised
 * hardest, so it is worth naming what each rule bought here:
 *
 * "Prefer drag-to-apply-force over button-to-push."  The bale follows your finger, and the
 *   distance you dragged becomes the impulse. Your finger *is* the force. There is still a
 *   "show me a big push" button, because a child who cannot drag must not be locked out.
 *
 * "A meter must account for ALL the energy."  Three buckets, and they are exhaustive: the energy
 *   in the push is either still motion, or already heat in the ground, or it went into the bump at
 *   the end. The total row sums the buckets, so if a bucket were ever missing the chart would
 *   visibly stop adding up instead of quietly losing energy.
 *
 * "Don't let the display contradict the lesson."  The speed readout goes through fmt(), which
 *   renders a small non-zero speed as "< 0.01" rather than "0". The POC's meter read zero while
 *   the box was still moving; that specific lie is impossible here.
 *
 * "Slow the simulation when the moment matters."  Constants are tuned so a firm push with the
 *   default mass and friction slides for about four seconds. The slowing-down *is* the lesson, so
 *   it has to be watchable.
 *
 * "Tune for the feeling."  A long arrow really does send it a long way: impulse is linear in drag
 *   distance, and the default mass is low enough that a full-width drag crosses the yard.
 */

import { clamp } from "../dom.js";
import {
  boolParam,
  draggable,
  loop,
  meter,
  numParam,
  readouts,
  scaffold,
  setAttrs,
  simButton,
  simTrack,
  strParam,
  svgEl,
  svgText,
  type Loop,
  type SimParams,
} from "./svg.js";
import type { Sim } from "./index.js";

const W = 640;
const H = 320;
const GROUND_Y = 250;
const BOX_W = 72;
const BOX_H = 54;
const LEFT_WALL = 24;
const RIGHT_WALL = W - 24;
/** Pixels per metre, so the readouts can use a unit a child has heard of. */
const PPM = 100;
/** Drag pixels → impulse. Tuned so a 200px drag at the default mass slides ~4.5m over ~4s. */
const IMPULSE_PER_PX = 3.4;
/** friction (0..1) → deceleration in px/s². */
const DECEL_PER_FRICTION = 140;

export function createPushAndFriction(params: SimParams, host: HTMLElement): Sim {
  let friction = numParam(params, "friction", 0.4, 0, 1);
  let mass = numParam(params, "mass", 3, 0.1, 20);
  let surface = strParam(params, "surface", "grass");
  const showMeter = boolParam(params, "showEnergyMeter", true);
  /**
   * What the pushed thing is called, in the two places a child reads it.
   *
   * Construction-time like `showEnergyMeter`, because `scaffold` writes the label and the hint once
   * and a lesson has no reason to rename the object mid-push.
   */
  const objectName = strParam(params, "object", "hay bale");

  const view = scaffold(host, {
    width: W,
    height: H,
    label: `A ${objectName} on the ground. Drag it to push it.`,
    hint: `Drag the ${objectName} to push it, then let go.`,
  });

  // ── scene ────────────────────────────────────────────────────────────────────
  const groundFill = svgEl("rect", {
    x: 0,
    y: GROUND_Y,
    width: W,
    height: H - GROUND_Y,
    fill: "var(--sim-ground)",
    opacity: 0.18,
  });
  const groundLine = svgEl("line", {
    x1: 0,
    y1: GROUND_Y,
    x2: W,
    y2: GROUND_Y,
    stroke: "var(--sim-ground)",
    "stroke-width": 4,
  });
  const wallLeft = svgEl("line", {
    x1: LEFT_WALL,
    y1: GROUND_Y,
    x2: LEFT_WALL,
    y2: GROUND_Y - 90,
    stroke: "var(--sim-ground)",
    "stroke-width": 6,
    "stroke-linecap": "round",
    opacity: 0.5,
  });
  const wallRight = svgEl("line", {
    x1: RIGHT_WALL,
    y1: GROUND_Y,
    x2: RIGHT_WALL,
    y2: GROUND_Y - 90,
    stroke: "var(--sim-ground)",
    "stroke-width": 6,
    "stroke-linecap": "round",
    opacity: 0.5,
  });

  const surfaceLabel = svgText(W / 2, H - 12, "", {
    "text-anchor": "middle",
    "font-size": 15,
    opacity: 0.75,
  });

  // The bale. Rounded, banded, and cursor: grab — it has to *look* like the thing you drag.
  const bale = svgEl("g", { class: "sim-bale" });
  const baleBody = svgEl("rect", {
    x: 0,
    y: 0,
    width: BOX_W,
    height: BOX_H,
    rx: 10,
    fill: "var(--sim-object)",
    stroke: "var(--sim-object-line)",
    "stroke-width": 3,
  });
  bale.appendChild(baleBody);
  for (let i = 1; i <= 3; i += 1) {
    bale.appendChild(
      svgEl("line", {
        x1: 6,
        y1: (BOX_H / 4) * i,
        x2: BOX_W - 6,
        y2: (BOX_H / 4) * i,
        stroke: "var(--sim-object-line)",
        "stroke-width": 2,
        opacity: 0.55,
      }),
    );
  }
  // A grip glyph, so "you can grab this" is stated and not merely implied.
  bale.appendChild(
    svgText(BOX_W / 2, BOX_H / 2 + 5, "⇔", {
      "text-anchor": "middle",
      "font-size": 20,
      fill: "var(--sim-object-line)",
    }),
  );

  const arrow = svgEl("g", { opacity: 0 });
  const arrowShaft = svgEl("line", {
    stroke: "var(--sim-hot)",
    "stroke-width": 8,
    "stroke-linecap": "round",
  });
  const arrowHead = svgEl("polygon", { fill: "var(--sim-hot)" });
  const arrowLabel = svgText(0, 0, "", {
    "text-anchor": "middle",
    "font-size": 15,
    "font-weight": "700",
    fill: "var(--sim-hot)",
  });
  arrow.appendChild(arrowShaft);
  arrow.appendChild(arrowHead);
  arrow.appendChild(arrowLabel);

  const bump = svgEl("g", { opacity: 0 });
  bump.appendChild(svgText(0, 0, "bump!", { "text-anchor": "middle", "font-size": 18, "font-weight": "700", fill: "var(--sim-hot)" }));

  for (const node of [groundFill, groundLine, wallLeft, wallRight, surfaceLabel, bale, arrow, bump]) {
    view.stage.appendChild(node);
  }

  const gauges = readouts(view.readouts, ["speed", "push", "ground"]);
  const energy = showMeter
    ? meter(view.extras, "Where the push energy went", [
        { key: "moving", label: "Still moving", colour: "var(--sim-cool)" },
        { key: "heat", label: "Heat (rubbing)", colour: "var(--sim-hot)" },
        { key: "bump", label: "The bump", colour: "var(--sim-neutral)" },
      ])
    : null;

  // ── state ────────────────────────────────────────────────────────────────────
  let x = (W - BOX_W) / 2;
  let v = 0;
  /** Energy in the last push, in arbitrary but internally consistent units. */
  let e0 = 0;
  let heat = 0;
  let bumped = 0;
  let lastPush = 0;
  let bumpFade = 0;

  let dragging = false;
  let dragFromX = 0;
  let dragOriginX = 0;

  function paint(): void {
    setAttrs(bale, { transform: `translate(${x} ${GROUND_Y - BOX_H})` });
    gauges.setNumber("speed", Math.abs(v) / PPM, 2, "m/s");
    gauges.setNumber("push", lastPush / PPM, 2, "m of push");
    gauges.set("ground", `${surface} — ${grip(friction)}`);
    surfaceLabel.textContent = `${surface} (${grip(friction)})`;
    if (energy) {
      const moving = 0.5 * mass * v * v;
      // moving + heat + bumped === e0 by construction; the total row proves it on screen.
      energy.update({ moving, heat, bump: bumped }, e0);
    }
    setAttrs(bump, { transform: `translate(${x + BOX_W / 2} ${GROUND_Y - BOX_H - 16})`, opacity: bumpFade });
  }

  function showArrow(fromX: number, toX: number): void {
    const y = GROUND_Y - BOX_H - 26;
    const dx = toX - fromX;
    const dir = dx >= 0 ? 1 : -1;
    const tip = toX;
    setAttrs(arrow, { opacity: Math.abs(dx) > 4 ? 1 : 0 });
    setAttrs(arrowShaft, { x1: fromX, y1: y, x2: tip - dir * 14, y2: y });
    setAttrs(arrowHead, {
      points: `${tip},${y} ${tip - dir * 16},${y - 9} ${tip - dir * 16},${y + 9}`,
    });
    setAttrs(arrowLabel, { x: (fromX + tip) / 2, y: y - 16 });
    arrowLabel.textContent = pushWord(Math.abs(dx));
  }

  function hideArrow(): void {
    setAttrs(arrow, { opacity: 0 });
  }

  function release(dragPx: number): void {
    lastPush = dragPx;
    // Same push on a heavier bale gives less speed. That is the mass lesson, kept honest.
    v = (IMPULSE_PER_PX * dragPx) / mass;
    e0 = 0.5 * mass * v * v;
    heat = 0;
    bumped = 0;
    simTrack(host, "drag-push", Number((dragPx / PPM).toFixed(2)));
  }

  const stopDrag = draggable(view.stage, bale, {
    onStart(point) {
      dragging = true;
      v = 0;
      dragFromX = point.x;
      dragOriginX = x;
      hideArrow();
    },
    onMove(point) {
      if (!dragging) return;
      const dx = point.x - dragFromX;
      x = clamp(dragOriginX + dx, LEFT_WALL, RIGHT_WALL - BOX_W);
      showArrow(dragOriginX + BOX_W / 2, x + BOX_W / 2);
      paint();
    },
    onEnd() {
      if (!dragging) return;
      dragging = false;
      const dragPx = x - dragOriginX;
      hideArrow();
      release(dragPx);
      paint();
    },
  });

  // ── controls ─────────────────────────────────────────────────────────────────
  view.controls.appendChild(
    simButton("Show me a big push", () => {
      x = LEFT_WALL + 8;
      paint();
      release(220);
      simTrack(host, "show-big-push", 2.2);
    }, true),
  );
  view.controls.appendChild(
    simButton("Put it back", () => {
      x = (W - BOX_W) / 2;
      v = 0;
      e0 = 0;
      heat = 0;
      bumped = 0;
      lastPush = 0;
      paint();
      simTrack(host, "reset");
    }),
  );

  /*
   * The caption names the meter's buckets, so it can only be said while the meter is on screen to
   * be looked at. With `showEnergyMeter: false` it was telling a child to watch something that is
   * not there — the display contradicting the lesson, which is the failure principles.md singles
   * out. The alternative states what the sim still visibly demonstrates without it.
   */
  view.caption.textContent = showMeter
    ? "The push energy never disappears. Watch it move out of “still moving” and into heat."
    : "The rougher the ground, the sooner it stops.";

  // ── integration ──────────────────────────────────────────────────────────────
  const decel = (): number => DECEL_PER_FRICTION * friction;

  const running: Loop = loop((dt) => {
    if (bumpFade > 0) bumpFade = Math.max(0, bumpFade - dt * 1.4);
    if (dragging || v === 0) {
      if (bumpFade > 0) paint();
      return;
    }
    const a = decel();
    x += v * dt;
    v = Math.sign(v) * Math.max(0, Math.abs(v) - a * dt);

    if (x <= LEFT_WALL || x >= RIGHT_WALL - BOX_W) {
      x = clamp(x, LEFT_WALL, RIGHT_WALL - BOX_W);
      // The rest of the motion energy goes into the bump: squashed hay and a thud. Without this
      // bucket the meter would visibly lose energy at the wall, which is the exact failure
      // principles.md describes.
      bumped += 0.5 * mass * v * v;
      v = 0;
      bumpFade = 1;
      simTrack(host, "hit-wall");
    }
    // Below half a pixel a second it has stopped moving in any sense a child can see.
    if (Math.abs(v) < 0.5 && a > 0) v = 0;

    // Heat is the residual: whatever is neither motion nor bump has gone into warming the ground.
    // Derived rather than integrated, so the three buckets sum to exactly the push energy no
    // matter what the integrator does. The meter cannot drift, and cannot lose energy.
    heat = Math.max(0, e0 - 0.5 * mass * v * v - bumped);
    paint();
  });

  paint();

  return {
    setParam(name, value) {
      if (name === "friction" && typeof value === "number") friction = clamp(value, 0, 1);
      else if (name === "mass" && typeof value === "number") mass = clamp(value, 0.1, 20);
      else if (name === "surface" && typeof value === "string") surface = value.slice(0, 40);
      // Structural: both are read once at construction, so there is nothing to repaint.
      else if (name === "showEnergyMeter" || name === "object") return;
      paint();
    },
    destroy() {
      running.stop();
      stopDrag();
      view.root.remove();
    },
  };
}

/** A word for how grippy the ground is, derived from friction so it cannot contradict it. */
function grip(friction: number): string {
  if (friction <= 0.001) return "perfectly slippery";
  if (friction < 0.15) return "very slippery";
  if (friction < 0.35) return "a bit slippery";
  if (friction < 0.6) return "quite grippy";
  return "very grippy";
}

function pushWord(px: number): string {
  if (px < 40) return "little push";
  if (px < 120) return "push";
  if (px < 220) return "big push";
  return "huge push";
}
